/**
 * @zendir/ui - ZenSpace3D rendering math
 *
 * The minimal helper set needed by the live 3D backends:
 *   • Earth + scene scaling constants used by the Three.js path
 *     (`ZenSpace3D.tsx`) and the Cesium capture source
 *     (`CesiumCaptureSource.ts`)
 *   • `latLonAltToCartesian` — lat/lon/alt → render-space Cartesian
 *   • `easeOutCubic` — camera-fly-to easing
 *
 * Higher-level orbital math (TLE/SGP4 propagation, GMST, visibility cones,
 * hex-grid coverage) was removed — those weren't consumed by either
 * backend. The Cesium backend uses Cesium's own `Transforms` for
 * ECI→ECEF, and the Three.js backend draws coverage from explicit
 * `SatelliteCoverage` props rather than computing it locally.
 */

import type { Vector3D } from "./ZenSpace3DTypes";

// ── Constants ─────────────────────────────────────────────────────────────────

/** Earth's mean radius in km. */
export const EARTH_RADIUS_KM = 6371;

/** Degrees → radians. */
export const DEG_TO_RAD = Math.PI / 180;

/** Radians → degrees. */
export const RAD_TO_DEG = 180 / Math.PI;

/** Earth radius in scene units (Three.js render space). */
export const SCENE_EARTH_RADIUS = 3000;

/** Conversion factor: 1 km → scene units. */
export const KM_TO_SCENE = SCENE_EARTH_RADIUS / EARTH_RADIUS_KM;

// ── Coordinate conversion ─────────────────────────────────────────────────────

/**
 * Convert lat/lon/alt to a Cartesian point in scene space.
 *
 * Result is in *render units* (scaled by `KM_TO_SCENE`), suitable for
 * direct use in the Three.js scene graph. For ECI→ECEF on the Cesium
 * backend, use `Cesium.Transforms.computeTemeToPseudoFixedMatrix`.
 *
 * @param lat        Latitude in degrees.
 * @param lon        Longitude in degrees.
 * @param altKm      Altitude above Earth surface in km (default 0).
 * @param earthRadius Scene radius for Earth (default `SCENE_EARTH_RADIUS`).
 */
export function latLonAltToCartesian(
  lat: number,
  lon: number,
  altKm: number = 0,
  earthRadius: number = SCENE_EARTH_RADIUS,
): Vector3D {
  const r = earthRadius + altKm * KM_TO_SCENE;
  const phi = (90 - lat) * DEG_TO_RAD;
  const theta = (lon + 180) * DEG_TO_RAD;
  return {
    x: -r * Math.sin(phi) * Math.cos(theta),
    y: r * Math.cos(phi),
    z: r * Math.sin(phi) * Math.sin(theta),
  };
}

// ── Easing ────────────────────────────────────────────────────────────────────

/** Cubic ease-out curve, used by camera fly-to animations. */
export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
